import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import '../css/AdminGroups.css';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import OpsLayout from '../components/OpsLayout';
import opsStatusTone from '../utils/opsStatusTone';
import AdminGroupMembers from '../components/AdminGroupMembers';

const GroupModal = ({ group, onClose }) => {
    const navigate = useNavigate();
    const [editedGroup, setEditedGroup] = useState(group || {
        name: '',
        description: '',
        available: true,
        group_role: 'MEMBER'
    });

    // 지역 및 병원 관련 상태 추가
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState('');
    const [selectedHospitalInfo, setSelectedHospitalInfo] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [showHospitalSelector, setShowHospitalSelector] = useState(false);

    // 모달 안 탭. 새 그룹(id 없음)에는 구성원 탭이 없어 늘 'info' 다. 모달은 열 때마다
    // 새로 마운트되므로(부모가 selectedGroup 이 없으면 렌더하지 않는다) 이 상태는
    // 열 때마다 자동으로 'info' 로 되돌아간다.
    const [modalTab, setModalTab] = useState('info');

    // group_role 선택 옵션 추가
    const GROUP_ROLES = [
        { value: 'MASTER', label: '총괄관리자' },
        { value: 'SUB_MASTER', label: '부관리자' },
        { value: 'MEMBER', label: '일반회원' },
        { value: 'CANDIDATE', label: '가입신청자' }
    ];

    useEffect(() => {
        const initializeData = async () => {
            const superAdminStatus = localStorage.getItem('is_super_admin') === 'true';
            setIsSuperAdmin(superAdminStatus);

            if (group) { // 수정 모드
                setShowHospitalSelector(false); // 수정 시에는 병원 변경 불가
                setEditedGroup({
                    name: group.group_name,
                    description: group.description,
                    available: group.available,
                    group_role: group.group_role
                });
                setSelectedRegion(group.region_name || '');
                setSelectedDistrict(group.district_name || '');
                setSelectedHospital(group.hospital_name || '');
                setSearchTerm(group.hospital_name || '');
                setSelectedHospitalInfo({
                    id: group.hospital_id,
                    institution_name: group.hospital_name,
                    type_name: group.hospital_type_name,
                    region_name: group.region_name,
                    district_name: group.district_name,
                    type_code: group.hospital_type_code,
                    region_code: group.region_code,
                    district_code: group.district_code,
                    postal_code: group.postal_code
                });
            } else { // 생성 모드
                if (superAdminStatus) {
                    setShowHospitalSelector(true);
                    fetchRegions();
                } else { // 일반 관리자
                    const adminGroupIds = JSON.parse(localStorage.getItem('admin_group_ids')) || [];
                    if (adminGroupIds.length > 0) {
                        try {
                            const token = localStorage.getItem('token');
                            const response = await axios.get(`${API_BASE_URL}/api/admin-groups`, {
                                headers: { Authorization: `Bearer ${token}` },
                                params: { admin_group_ids: adminGroupIds }
                            });
                            if (response.data.success && response.data.groups) {
                                const hospitalIds = new Set(response.data.groups.map(g => g.hospital_id));
                                if (hospitalIds.size > 1) {
                                    setShowHospitalSelector(true);
                                    fetchRegions();
                                } else {
                                    setShowHospitalSelector(false);
                                    // 기존 단일 병원 로직
                                    const regionName = localStorage.getItem('region_name') || '';
                                    const districtName = localStorage.getItem('district_name') || '';
                                    const hospitalName = localStorage.getItem('hospital_name') || '';
                                    const hospitalId = localStorage.getItem('hospital_id') || '';
                                    setSelectedRegion(regionName);
                                    setSelectedDistrict(districtName);
                                    setSelectedHospital(hospitalName);
                                    setSearchTerm(hospitalName);
                                    setSelectedHospitalInfo({
                                        id: hospitalId,
                                        institution_name: hospitalName,
                                        // localStorage에서 더 많은 정보 가져오기
                                        type_name: localStorage.getItem('hospital_type_name') || '',
                                        region_name: regionName,
                                        district_name: districtName,
                                        type_code: localStorage.getItem('hospital_type_code') || '',
                                        region_code: localStorage.getItem('region_code') || '',
                                        district_code: localStorage.getItem('district_code') || '',
                                        postal_code: localStorage.getItem('postal_code') || '',
                                    });
                                }
                            }
                        } catch (error) {
                            console.error("Admin groups for hospital count check failed:", error);
                            setShowHospitalSelector(false); // 에러 발생 시 기본 UI
                        }
                    } else {
                        setShowHospitalSelector(false);
                    }
                }
            }
        };
        initializeData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group]);

   // 지역 목록 가져오기
   const fetchRegions = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('토큰이 없습니다.');
                return;
            }

            const response = await axios.get(`${API_BASE_URL}/api/regions`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setRegions(response.data.regions);
            } else {
                console.error('지역 목록 가져오기 실패:', response.data.message);
            }
        } catch (error) {
            console.error('지역 목록 가져오기 실패:', error);
        }
    };

    // 지역 선택 핸들러
    const handleRegionChange = async (e) => {
        const region = e.target.value;
        setSelectedRegion(region);
        setSelectedDistrict('');
        setSearchResults([]);
        setSearchTerm('');
        setSelectedHospital('');
        setEditedGroup(prev => ({ ...prev, region: region, hospital: '' }));

        if (region) {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.error('토큰이 없습니다.');
                    return;
                }

                const response = await axios.get(`${API_BASE_URL}/api/districts/${encodeURIComponent(region)}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data.success) {
                    setDistricts(response.data.districts);
                } else {
                    console.error('구/군 목록 가져오기 실패:', response.data.message);
                    setDistricts([]);
                }
            } catch (error) {
                console.error('구/군 목록 가져오기 실패:', error);
                setDistricts([]);
            }
        } else {
            setDistricts([]);
        }
    };

    // 구/군 선택 핸들러
    const handleDistrictChange = (e) => {
        setSelectedDistrict(e.target.value);
        setSearchResults([]);
        setSearchTerm('');
        setSelectedHospital('');
    };

    // 병원 검색 핸들러
    const handleHospitalSearch = async () => {
        if (!selectedRegion || !searchTerm.trim()) {
            alert('지역을 선택하고 병원명을 입력해주세요.');
            return;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/api/hospitals/search`, {
                params: {
                    region_name: selectedRegion,
                    district_name: selectedDistrict,
                    search_term: searchTerm
                }
            });

            if (response.data.success) {
                const hospitals = response.data.hospitals.map(hospital => {
                    // 필수 필드 확인
                    const requiredFields = ['id', 'institution_name', 'type_code', 'region_code', 'district_code', 'postal_code'];
                    const missingFields = requiredFields.filter(field => !hospital[field]);

                    if (missingFields.length > 0) {
                        console.error('병원 데이터에 누락된 필수 필드:', missingFields, hospital);
                    }

                    return {
                        id: hospital.id,
                        institution_name: hospital.institution_name,
                        type_name: hospital.type_name,
                        region_name: hospital.region_name,
                        district_name: hospital.district_name,
                        subdistrict: hospital.subdistrict || '',
                        address: hospital.address,
                        type_code: hospital.type_code,
                        region_code: hospital.region_code,
                        district_code: hospital.district_code,
                        postal_code: hospital.postal_code
                    };
                });
                setSearchResults(hospitals);
            } else {
                setSearchResults([]);
                alert('병원 검색 결과가 없습니다.');
            }
        } catch (error) {
            console.error('병원 검색 실패:', error);
            setSearchResults([]);
            alert('병원 검색 중 오류가 발생했습니다.');
        }
    };

    // 병원 선택 핸들러
    const handleHospitalSelect = (hospital) => {
        // 필수 필드 확인
        const requiredFields = ['id', 'institution_name', 'type_code', 'region_code', 'district_code', 'postal_code'];
        const missingFields = requiredFields.filter(field => !hospital[field]);

        if (missingFields.length > 0) {
            console.error('병원 정보에 누락된 필수 필드:', missingFields);
            alert(`병원 정보에 필수 데이터가 누락되었습니다: ${missingFields.join(', ')}`);
            return;
        }

        setSelectedHospital(hospital.institution_name);
        setSearchTerm(hospital.institution_name);
        setSearchResults([]);
        setSelectedHospitalInfo(hospital);
        setEditedGroup(prev => ({
            ...prev,
            hospital: hospital.institution_name,
            region: hospital.region_name,
            district: hospital.district_name
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedGroup(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            // 필수 필드 검증
            if (!editedGroup.name.trim()) {
                alert('그룹명을 입력해주세요.');
                return;
            }

            if (!editedGroup.group_role) {
                alert('그룹 역할을 선택해주세요.');
                return;
            }

            // 새 그룹 생성 시에만 병원 정보 필수 체크
            if (!group && (!selectedRegion || !selectedHospital)) {
                alert('지역과 병원을 선택해주세요.');
                return;
            }

            if (!group && !selectedHospitalInfo) {
                alert('병원을 다시 선택해주세요.');
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                alert('로그인이 필요합니다.');
                navigate('/login');
                return;
            }

            const saveData = {
                group_name: editedGroup.name,
                description: editedGroup.description || '',
                group_role: editedGroup.group_role,
                available: editedGroup.available
            };

            // 기존 그룹 수정 시 기존 병원 정보 유지
            if (group) {
                saveData.hospital_id = group.hospital_id;
                saveData.region_code = group.region_code;
                saveData.district_code = group.district_code;
                saveData.postal_code = group.postal_code;
                saveData.hospital_type_code = group.hospital_type_code;
            } else {
                // 새 그룹 생성 시에만 새로운 병원 정보 사용
                let hospitalInfo = selectedHospitalInfo;
                // postal_code 또는 type_code가 없으면 병원 상세 조회
                if (!hospitalInfo.postal_code || !hospitalInfo.type_code) {
                    try {
                        const res = await axios.get(`${API_BASE_URL}/api/hospitals/info`, {
                            params: {
                                institution_name: hospitalInfo.institution_name,
                                region_name: hospitalInfo.region_name
                            }
                        });
                        if (res.data && res.data.success && res.data.hospital) {
                            hospitalInfo = res.data.hospital;
                        }
                    } catch (err) {
                        alert('병원 상세 정보를 불러오지 못했습니다.');
                        return;
                    }
                }
                saveData.hospital_id = hospitalInfo.id;
                saveData.region_code = hospitalInfo.region_code;
                saveData.district_code = hospitalInfo.district_code;
                saveData.postal_code = hospitalInfo.postal_code ? hospitalInfo.postal_code : null;
                saveData.hospital_type_code = hospitalInfo.type_code ? hospitalInfo.type_code : null;
            }

            let response;
            if (group) {
                response = await axios.put(
                    `${API_BASE_URL}/api/admin-groups/${group.id}`,
                    saveData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
            } else {
                response = await axios.post(
                    `${API_BASE_URL}/api/admin-groups`,
                    saveData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
            }

            if (response.data.success) {
                alert(response.data.message || (group ? '그룹이 수정되었습니다.' : '그룹이 생성되었습니다.'));
                onClose();
                window.location.reload();
            } else {
                alert(response.data.message || '저장 중 오류가 발생했습니다.');
            }
        } catch (error) {
            console.error('그룹 저장 실패:', error);

            if (error.response?.status === 401) {
                alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
                localStorage.removeItem('token');
                navigate('/login');
            } else {
                alert(error.response?.data?.message || '저장 중 오류가 발생했습니다.');
            }
        }
    };

    const handleDelete = async () => {
        if (!group) return;

        if (!window.confirm('정말로 이 그룹을 삭제하시겠습니까?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${API_BASE_URL}/api/admin-groups/${group.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                alert(response.data.message || '그룹이 삭제되었습니다.');
                onClose();
                window.location.reload();
            } else {
                alert(response.data.message || '삭제 중 오류가 발생했습니다.');
            }
        } catch (error) {
            console.error('그룹 삭제 실패:', error);
            alert(error.response?.data?.message || '삭제 중 오류가 발생했습니다.');
        }
    };

    const showInfoTab = modalTab === 'info' || !group?.id;

    return (
        <div className="sds-modal-overlay" onClick={onClose}>
            <div
                className="sds-modal sds-modal--wide"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="group-modal-title"
            >
                <div className="sds-modal__head">
                    <h2 id="group-modal-title" className="sds-modal__title">
                        {group?.id ? group.group_name : '관리자 그룹 생성'}
                    </h2>
                    <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={onClose} aria-label="닫기">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>

                {/* 새 그룹에는 구성원 탭이 없다 — 저장해서 id 가 생긴 뒤에 열린다(spec D1). */}
                {group?.id && (
                    <div className="sds-tabs admin-groups-page__tabs" role="tablist">
                        <button
                            role="tab"
                            aria-selected={modalTab === 'info'}
                            className={`sds-tabs__item${modalTab === 'info' ? ' is-active' : ''}`}
                            onClick={() => setModalTab('info')}
                        >
                            그룹 정보
                        </button>
                        <button
                            role="tab"
                            aria-selected={modalTab === 'members'}
                            className={`sds-tabs__item${modalTab === 'members' ? ' is-active' : ''}`}
                            onClick={() => setModalTab('members')}
                        >
                            구성원
                        </button>
                    </div>
                )}

                <div className="sds-modal__body">
                    {showInfoTab ? (
                        <>
                            <div className="sds-field">
                                <label className="sds-label">그룹명<span className="sds-required">*</span></label>
                                <input
                                    name="name"
                                    className="sds-input"
                                    value={editedGroup.name}
                                    onChange={handleChange}
                                    placeholder="그룹명을 입력하세요"
                                />
                            </div>
                            <div className="sds-field">
                                <label className="sds-label">설명</label>
                                <textarea
                                    name="description"
                                    className="sds-textarea"
                                    value={editedGroup.description}
                                    onChange={handleChange}
                                    placeholder="그룹 설명을 입력하세요"
                                />
                            </div>
                            <div className="sds-field">
                                <label className="sds-label">그룹 역할<span className="sds-required">*</span></label>
                                <select
                                    name="group_role"
                                    className="sds-select"
                                    value={editedGroup.group_role}
                                    onChange={handleChange}
                                >
                                    {GROUP_ROLES.map(role => (
                                        <option key={role.value} value={role.value}>
                                            {role.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="sds-field">
                                <label className="sds-label">상태</label>
                                <div className="sds-seg" role="radiogroup" aria-label="상태">
                                    {[[true, '활성'], [false, '비활성']].map(([value, label]) => {
                                        const active = editedGroup.available === value;
                                        return (
                                            <button
                                                key={label}
                                                role="radio"
                                                aria-checked={active}
                                                className={`sds-seg__item${active ? ' is-active' : ''}`}
                                                onClick={() => setEditedGroup(prev => ({ ...prev, available: value }))}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {showHospitalSelector && (
                                <>
                                    <div className="sds-field">
                                        <label className="sds-label">지역</label>
                                        <div className="admin-groups-page__location">
                                            <select className="sds-select" value={selectedRegion} onChange={handleRegionChange}>
                                                <option value="">시/도 선택</option>
                                                {regions.map((region) => (
                                                    <option key={region.region_code} value={region.region_name}>
                                                        {region.region_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <select
                                                className="sds-select"
                                                value={selectedDistrict}
                                                onChange={handleDistrictChange}
                                                disabled={!selectedRegion}
                                            >
                                                <option value="">전체 구/군</option>
                                                {districts.map((district) => (
                                                    <option key={district.district_code} value={district.district_name}>
                                                        {district.district_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="sds-field">
                                        <label className="sds-label">병원 검색</label>
                                        <div className="sds-toolbar">
                                            <input
                                                className="sds-input admin-groups-page__search"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="병원명을 입력하세요"
                                            />
                                            <button className="sds-btn sds-btn--secondary" onClick={handleHospitalSearch}>검색</button>
                                        </div>
                                        {searchResults.length > 0 && (
                                            <div className="sds-list admin-groups-page__hospital-results">
                                                {searchResults.map((hospital) => (
                                                    <div
                                                        key={hospital.id}
                                                        className="sds-list__row sds-list__row--clickable"
                                                        onClick={() => handleHospitalSelect(hospital)}
                                                    >
                                                        <div className="sds-list__body">
                                                            <b>{hospital.institution_name}</b>
                                                            <small>{hospital.region_name} {hospital.district_name}</small>
                                                        </div>
                                                        <span className="sds-list__chev">›</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {selectedHospitalInfo && (
                                <div className="sds-field">
                                    <label className="sds-label">병원 정보</label>
                                    <dl className="ops-page__kv">
                                        <dt>병원명</dt><dd>{selectedHospitalInfo.institution_name}</dd>
                                        <dt>지역</dt><dd>{selectedHospitalInfo.region_name}</dd>
                                        <dt>구/군</dt><dd>{selectedHospitalInfo.district_name}</dd>
                                    </dl>
                                </div>
                            )}
                        </>
                    ) : (
                        <AdminGroupMembers groupId={group.id} />
                    )}
                </div>

                <div className="sds-modal__foot">
                    {showInfoTab ? (
                        <>
                            {group && (
                                <button className="sds-btn sds-btn--danger" onClick={handleDelete}>삭제</button>
                            )}
                            <span className="sds-toolbar__spacer" />
                            <button className="sds-btn sds-btn--secondary" onClick={onClose}>취소</button>
                            <button className="sds-btn sds-btn--primary" onClick={handleSave}>{group ? '수정' : '생성'}</button>
                        </>
                    ) : (
                        <button className="sds-btn sds-btn--secondary" onClick={onClose}>닫기</button>
                    )}
                </div>
            </div>
        </div>
    );
};

const GROUP_ROLES = [
    { value: 'MASTER', label: '총괄관리자' },
    { value: 'SUB_MASTER', label: '부관리자' },
    { value: 'MEMBER', label: '일반회원' },
    { value: 'CANDIDATE', label: '가입신청자' }
];

export default function AdminGroups() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const groupsPerPage = 10;
    const [filterType, setFilterType] = useState('all');

    const getRoleLabel = (roleValue) => {
        const role = GROUP_ROLES.find(r => r.value === roleValue);
        return role ? role.label : roleValue;
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;

            if (decodedToken.exp < currentTime) {
                localStorage.removeItem('token');
                navigate('/login');
                return;
            }
        } catch (error) {
            localStorage.removeItem('token');
            navigate('/login');
            return;
        }

        fetchGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    const fetchGroups = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';
            let params = { is_super_admin: isSuperAdmin ? 'true' : 'false' };

            // 일반 관리자일 때만 추가 파라미터 설정
            if (!isSuperAdmin) {
                const adminGroupIds = JSON.parse(localStorage.getItem('admin_group_ids'));
                if (adminGroupIds && adminGroupIds.length > 0) {
                    params.admin_group_ids = adminGroupIds;
                } else {
                    // 호환성을 위해 기존 파라미터 유지
                    params.hospital_id = localStorage.getItem('hospital_id');
                    params.region_code = localStorage.getItem('region_code');
                    params.district_code = localStorage.getItem('district_code');
                }
            }

            const response = await axios.get(`${API_BASE_URL}/api/admin-groups`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                params
            });

            if (response.data && Array.isArray(response.data.groups)) {
                setGroups(response.data.groups);
            } else {
                setGroups([]);
            }
        } catch (error) {
            console.error('그룹 목록 조회 중 오류 발생:', error);
            setError('그룹 목록을 불러오는 중 오류가 발생했습니다.');
            setGroups([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearch = () => {
        fetchGroups();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleGroupClick = (group) => {
        setSelectedGroup(group);
    };

    const handleCloseModal = () => {
        setSelectedGroup(null);
        setShowCreateModal(false);
    };

    const indexOfLastGroup = currentPage * groupsPerPage;
    const indexOfFirstGroup = indexOfLastGroup - groupsPerPage;
    const currentGroups = groups.slice(indexOfFirstGroup, indexOfLastGroup);
    const totalPages = Math.ceil(groups.length / groupsPerPage);

    // super_admin 여부 확인
    const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

    // 일괄 생성 엑셀 업로드 핸들러
    const handleBulkCreateClick = () => {
        document.getElementById('admin-groups-bulk-file-input').click();
    };
    const handleBulkFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Sheet1에서 병원, 주소, 그룹명 정보 추출
                const sheet1 = workbook.Sheets['Sheet1'];
                if (!sheet1) {
                    alert('Sheet1을 찾을 수 없습니다.');
                    return;
                }

                // Sheet2에서 관리자 이름, 관리자 ID 정보 추출
                const sheet2 = workbook.Sheets['Sheet2'];
                if (!sheet2) {
                    alert('Sheet2를 찾을 수 없습니다.');
                    return;
                }

                // Sheet3에서 사용자 이름, 사용자 전화번호 정보 추출
                const sheet3 = workbook.Sheets['Sheet3'];
                if (!sheet3) {
                    alert('Sheet3를 찾을 수 없습니다.');
                    return;
                }

                const sheet1Data = XLSX.utils.sheet_to_json(sheet1);
                const sheet2Data = XLSX.utils.sheet_to_json(sheet2);
                const sheet3Data = XLSX.utils.sheet_to_json(sheet3);

                const groupInfos = sheet1Data.map(row => ({
                    hospital: row['병원'],
                    address: row['주소'],
                    groupName: row['그룹명']
                })).filter(row => row.hospital && row.address && row.groupName);

                const adminInfos = sheet2Data.map(row => ({
                    adminName: row['관리자 이름'],
                    userEmail: row['관리자 ID']  // center_admin_info 테이블의 user_email 컬럼
                })).filter(row => row.adminName && row.userEmail);

                const userInfos = sheet3Data.map(row => ({
                    userName: row['사용자 이름'],
                    userPhone: row['사용자 전화번호']
                })).filter(row => row.userName && row.userPhone);

                if (groupInfos.length === 0) {
                    alert('Sheet1에 유효한 데이터가 없습니다. 병원, 주소, 그룹명이 모두 입력되어야 합니다.');
                    return;
                }

                if (adminInfos.length === 0) {
                    alert('Sheet2에 유효한 데이터가 없습니다. 관리자 이름, 관리자 ID(이메일)가 모두 입력되어야 합니다.');
                    return;
                }

                if (userInfos.length === 0) {
                    alert('Sheet3에 유효한 데이터가 없습니다. 사용자 이름, 사용자 전화번호가 모두 입력되어야 합니다.');
                    return;
                }

                // 서버로 전송
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.post(
                        `${API_BASE_URL}/api/admin-groups/bulk-create`,
                        { groupInfos, adminInfos, userInfos },
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (response.data.success) {
                        alert('관리자 그룹이 일괄 생성되었습니다.');
                        fetchGroups();
                    } else {
                        alert(response.data.message || '일괄 생성에 실패했습니다.');
                    }
                } catch (err) {
                    console.error('일괄 생성 오류:', err);
                    alert('서버와의 통신 중 오류가 발생했습니다.');
                }
            };
            reader.readAsArrayBuffer(file);
        } catch (error) {
            console.error('파일 처리 오류:', error);
            alert('파일 처리 중 오류가 발생했습니다.');
        }
    };

    return (
        <OpsLayout
            title="관리자 그룹 관리"
            description="기관별 관리자 그룹을 만들고 구성원을 정합니다."
            actions={
                <>
                    {isSuperAdmin && (
                        <>
                            <button className="sds-btn sds-btn--secondary" onClick={handleBulkCreateClick}>
                                관리자 그룹 일괄 생성
                            </button>
                            <input
                                id="admin-groups-bulk-file-input"
                                type="file"
                                accept=".xlsx"
                                hidden
                                onChange={handleBulkFileChange}
                            />
                        </>
                    )}
                    <button className="sds-btn sds-btn--primary" onClick={() => setShowCreateModal(true)}>
                        <Plus size={16} aria-hidden="true" />새 그룹 생성
                    </button>
                </>
            }
        >
            <section className="ops-page__card admin-groups-page">
                <div className="sds-toolbar">
                    <input
                        className="sds-input admin-groups-page__search"
                        placeholder="그룹명으로 검색..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyPress={handleKeyPress}
                    />
                    <button className="sds-btn sds-btn--secondary" onClick={handleSearch}>검색</button>
                </div>

                {isLoading ? (
                    <div className="sds-notice">데이터를 불러오는 중입니다...</div>
                ) : error ? (
                    <div className="sds-notice sds-notice--error">{error}</div>
                ) : (
                    <>
                        <div className="sds-table-wrap">
                            <table className="sds-table">
                                <thead>
                                    <tr>
                                        <th>번호</th>
                                        <th>그룹명</th>
                                        <th>설명</th>
                                        <th>병원명</th>
                                        <th>지역</th>
                                        <th>구/군</th>
                                        <th>그룹 역할</th>
                                        <th>상태</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentGroups.length > 0 ? (
                                        currentGroups.map((group, index) => {
                                            const statusLabel = group.available === true ? '활성' : '비활성';
                                            return (
                                                <tr
                                                    key={group.id}
                                                    onClick={() => handleGroupClick(group)}
                                                    className="ops-page__row--clickable"
                                                >
                                                    <td>{indexOfFirstGroup + index + 1}</td>
                                                    <td><b>{group.group_name}</b></td>
                                                    <td>{group.description}</td>
                                                    <td>{group.hospital_name}</td>
                                                    <td>{group.region_name}</td>
                                                    <td>{group.district_name}</td>
                                                    <td>{getRoleLabel(group.group_role)}</td>
                                                    <td>
                                                        <span className={`sds-badge sds-badge--${opsStatusTone(statusLabel)}`}>{statusLabel}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="sds-empty">데이터가 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="sds-pagination">
                            <button
                                className="sds-pagination__btn"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                aria-label="이전 페이지"
                            >
                                <ChevronLeft size={16} aria-hidden="true" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    className={`sds-pagination__btn${currentPage === page ? ' is-active' : ''}`}
                                    aria-current={currentPage === page ? 'page' : undefined}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className="sds-pagination__btn"
                                onClick={() => setCurrentPage(prev => Math.max(1, Math.min(prev + 1, totalPages)))}
                                disabled={currentPage >= totalPages}
                                aria-label="다음 페이지"
                            >
                                <ChevronRight size={16} aria-hidden="true" />
                            </button>
                        </div>
                    </>
                )}
            </section>

            {(selectedGroup || showCreateModal) && (
                <GroupModal
                    group={selectedGroup}
                    onClose={handleCloseModal}
                />
            )}
        </OpsLayout>
    );
}
